import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Download, Calendar, Sparkles, TrendingUp, DollarSign, Play, Heart, Share2, MessageCircle, BarChart3, Instagram, Music2, Tv, Radio, AudioWaveform, MapPin, Users, Smartphone, ArrowLeft, FileText, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { BarChart, Bar, LineChart, Line, ScatterChart, Scatter, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart } from 'recharts';
import { cn } from './ui/utils';

type ViewLevel = 'campaign' | 'beat' | 'adset' | 'ad' | 'charts';

type BeatType = 'paid-social' | 'influencer' | 'ctv' | 'dooh' | 'audio' | 'olv';

interface CampaignData {
  id: number;
  name: string;
  status: 'Active' | 'Paused' | 'Completed';
  startDate: string;
  endDate: string;
  totalBudget: number;
  primaryKPI: string;
  metrics: {
    totalSpend: number;
    totalImpressions: number;
    blendedCPM: number;
    totalReach: number;
    totalClicks: number;
    blendedCTR: number;
    totalEngagements: number;
    blendedCPE: number;
    totalVideoViews: number;
    blendedCPV: number;
  };
  beats?: BeatData[];
}

interface BeatData {
  id: number;
  name: string;
  type: BeatType;
  platform: string;
  status: 'Active' | 'Paused' | 'Completed';
  budget: number;
  startDate: string;
  endDate: string;
  metrics: {
    spend: number;
    impressions: number;
    cpm: number;
    reach: number;
    frequency: number;
    clicks: number;
    ctr: number;
    cpc: number;
    // Video & CTV specific
    videoViews?: number;
    cpv?: number;
    videoCompletes?: number;
    vtr?: number;
    cpcv?: number;
    // Social & Influencer specific
    totalEngagements?: number;
    engagementRate?: number;
    cpe?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    saves?: number;
    // Audio & DOOH specific
    plays?: number;
    ltr?: number;
  };
  adsets?: AdsetData[];
}

interface AdsetData {
  id: number;
  name: string;
  beatName: string;
  targetingSummary: string;
  geo: string;
  placement: string;
  deviceType: string;
  metrics: BeatData['metrics'];
  ads?: AdData[];
}

interface AdData {
  id: number;
  name: string;
  adsetName: string;
  creativeThumbnail: string;
  creativeId: string;
  format: string;
  influencerHandle?: string;
  deliverableStatus?: string;
  metrics: BeatData['metrics'] & {
    videoViews25?: number;
    videoViews50?: number;
    videoViews75?: number;
    avgWatchTime?: string;
  };
}

export function DashboardsPage() {
  const { t } = useTranslation();
  const [viewLevel, setViewLevel] = useState<ViewLevel>('campaign');
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [selectedBeat, setSelectedBeat] = useState<number | null>(null);
  const [selectedAdset, setSelectedAdset] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [dateRange, setDateRange] = useState('last-30-days');
  const [beatTypeFilter, setBeatTypeFilter] = useState<string>('all');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [campaignSubPage, setCampaignSubPage] = useState<
    'overview' | 'suggestions' | 'publications' | 'contentApproval' | 'results'
  >('overview');

  // Mock data
  const campaigns: CampaignData[] = [
    {
      id: 1,
      name: 'Merrell Urban Hiking Campaign',
      status: 'Active',
      startDate: '2024-01-15',
      endDate: '2024-12-31',
      totalBudget: 875000,
      primaryKPI: 'Reach & Engagement',
      metrics: {
        totalSpend: 724580,
        totalImpressions: 48250000,
        blendedCPM: 15.02,
        totalReach: 18400000,
        totalClicks: 156800,
        blendedCTR: 0.325,
        totalEngagements: 892400,
        blendedCPE: 0.81,
        totalVideoViews: 12450000,
        blendedCPV: 0.058
      },
      beats: [
        {
          id: 1,
          name: 'TikTok Organic Influencers',
          type: 'influencer',
          platform: 'TikTok',
          status: 'Active',
          budget: 250000,
          startDate: '2024-01-15',
          endDate: '2024-12-31',
          metrics: {
            spend: 218500,
            impressions: 15420000,
            cpm: 14.17,
            reach: 6250000,
            frequency: 2.47,
            clicks: 42800,
            ctr: 0.278,
            cpc: 5.11,
            totalEngagements: 524200,
            engagementRate: 3.4,
            cpe: 0.42,
            likes: 382100,
            comments: 48200,
            shares: 62400,
            saves: 31500
          },
          adsets: [
            {
              id: 101,
              name: 'Outdoor Enthusiasts 25-34',
              beatName: 'TikTok Organic Influencers',
              targetingSummary: 'Age 25-34, Interest: Hiking, Outdoor Activities',
              geo: 'US, CA, UK',
              placement: 'TikTok Feed',
              deviceType: 'Mobile',
              metrics: {
                spend: 89400,
                impressions: 6280000,
                cpm: 14.24,
                reach: 2540000,
                frequency: 2.47,
                clicks: 18200,
                ctr: 0.29,
                cpc: 4.91,
                totalEngagements: 221800,
                engagementRate: 3.53,
                cpe: 0.40,
                likes: 162400,
                comments: 19800,
                shares: 26200,
                saves: 13400
              },
              ads: [
                {
                  id: 1001,
                  name: '@hikewithme - Trail Running Series',
                  adsetName: 'Outdoor Enthusiasts 25-34',
                  creativeThumbnail: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400',
                  creativeId: 'TT-CR-001',
                  format: 'Video - Vertical',
                  influencerHandle: '@hikewithme',
                  deliverableStatus: 'Published',
                  metrics: {
                    spend: 28400,
                    impressions: 1980000,
                    cpm: 14.34,
                    reach: 820000,
                    frequency: 2.41,
                    clicks: 5840,
                    ctr: 0.295,
                    cpc: 4.86,
                    totalEngagements: 71200,
                    engagementRate: 3.60,
                    cpe: 0.40,
                    likes: 52100,
                    comments: 6400,
                    shares: 8500,
                    saves: 4200,
                    videoViews: 1680000,
                    cpv: 0.017,
                    videoViews25: 1480000,
                    videoViews50: 1180000,
                    videoViews75: 840000,
                    vtr: 84.8,
                    avgWatchTime: '18.2s'
                  }
                },
                {
                  id: 1002,
                  name: '@outdooradventurer - Urban Hikes',
                  adsetName: 'Outdoor Enthusiasts 25-34',
                  creativeThumbnail: 'https://images.unsplash.com/photo-1445308394109-4ec2920981b1?w=400',
                  creativeId: 'TT-CR-002',
                  format: 'Video - Vertical',
                  influencerHandle: '@outdooradventurer',
                  deliverableStatus: 'Published',
                  metrics: {
                    spend: 31200,
                    impressions: 2180000,
                    cpm: 14.31,
                    reach: 895000,
                    frequency: 2.44,
                    clicks: 6520,
                    ctr: 0.299,
                    cpc: 4.79,
                    totalEngagements: 78400,
                    engagementRate: 3.60,
                    cpe: 0.40,
                    likes: 57600,
                    comments: 7100,
                    shares: 9200,
                    saves: 4500,
                    videoViews: 1850000,
                    cpv: 0.017,
                    videoViews25: 1640000,
                    videoViews50: 1310000,
                    videoViews75: 930000,
                    vtr: 84.8,
                    avgWatchTime: '19.1s'
                  }
                },
                {
                  id: 1003,
                  name: '@trailblazer_jane - Weekend Trails',
                  adsetName: 'Outdoor Enthusiasts 25-34',
                  creativeThumbnail: 'https://images.unsplash.com/photo-1501555088652-021faa106b9b?w=400',
                  creativeId: 'TT-CR-003',
                  format: 'Video - Vertical',
                  influencerHandle: '@trailblazer_jane',
                  deliverableStatus: 'Published',
                  metrics: {
                    spend: 29800,
                    impressions: 2120000,
                    cpm: 14.06,
                    reach: 825000,
                    frequency: 2.57,
                    clicks: 5840,
                    ctr: 0.275,
                    cpc: 5.10,
                    totalEngagements: 72200,
                    engagementRate: 3.41,
                    cpe: 0.41,
                    likes: 52700,
                    comments: 6300,
                    shares: 8500,
                    saves: 4700,
                    videoViews: 1790000,
                    cpv: 0.017,
                    videoViews25: 1590000,
                    videoViews50: 1260000,
                    videoViews75: 890000,
                    vtr: 84.4,
                    avgWatchTime: '17.8s'
                  }
                }
              ]
            },
            {
              id: 102,
              name: 'Urban Adventurers 18-24',
              beatName: 'TikTok Organic Influencers',
              targetingSummary: 'Age 18-24, Interest: Fitness, Travel, Urban Lifestyle',
              geo: 'US Metro Areas',
              placement: 'TikTok Feed',
              deviceType: 'Mobile',
              metrics: {
                spend: 72800,
                impressions: 5140000,
                cpm: 14.16,
                reach: 2080000,
                frequency: 2.47,
                clicks: 14200,
                ctr: 0.276,
                cpc: 5.13,
                totalEngagements: 174600,
                engagementRate: 3.40,
                cpe: 0.42,
                likes: 127700,
                comments: 15200,
                shares: 20400,
                saves: 11300
              },
              ads: [
                {
                  id: 1004,
                  name: '@cityexplorer - Downtown Trails',
                  adsetName: 'Urban Adventurers 18-24',
                  creativeThumbnail: 'https://images.unsplash.com/photo-1464207687429-7505649dae38?w=400',
                  creativeId: 'TT-CR-004',
                  format: 'Video - Vertical',
                  influencerHandle: '@cityexplorer',
                  deliverableStatus: 'Published',
                  metrics: {
                    spend: 36400,
                    impressions: 2570000,
                    cpm: 14.16,
                    reach: 1040000,
                    frequency: 2.47,
                    clicks: 7100,
                    ctr: 0.276,
                    cpc: 5.13,
                    totalEngagements: 87300,
                    engagementRate: 3.40,
                    cpe: 0.42,
                    likes: 63900,
                    comments: 7600,
                    shares: 10200,
                    saves: 5600,
                    videoViews: 2180000,
                    cpv: 0.017,
                    videoViews25: 1920000,
                    videoViews50: 1530000,
                    videoViews75: 1090000,
                    vtr: 84.9,
                    avgWatchTime: '18.5s'
                  }
                }
              ]
            }
          ]
        },
        {
          id: 2,
          name: 'Meta Paid Social',
          type: 'paid-social',
          platform: 'Meta',
          status: 'Active',
          budget: 180000,
          startDate: '2024-02-01',
          endDate: '2024-11-30',
          metrics: {
            spend: 165200,
            impressions: 12800000,
            cpm: 12.91,
            reach: 4850000,
            frequency: 2.64,
            clicks: 38900,
            ctr: 0.304,
            cpc: 4.25,
            videoViews: 3280000,
            cpv: 0.050,
            videoCompletes: 1520000,
            vtr: 46.34,
            cpcv: 0.109,
            totalEngagements: 189400,
            engagementRate: 1.48,
            cpe: 0.87,
            likes: 125800,
            comments: 18200,
            shares: 28400,
            saves: 17000
          }
        },
        {
          id: 3,
          name: 'CTV Programmatic',
          type: 'ctv',
          platform: 'The Trade Desk',
          status: 'Active',
          budget: 200000,
          startDate: '2024-03-01',
          endDate: '2024-12-31',
          metrics: {
            spend: 186400,
            impressions: 8450000,
            cpm: 22.06,
            reach: 3200000,
            frequency: 2.64,
            clicks: 0,
            ctr: 0,
            cpc: 0,
            videoViews: 7880000,
            cpv: 0.024,
            videoCompletes: 4920000,
            vtr: 62.43,
            cpcv: 0.038
          }
        },
        {
          id: 4,
          name: 'DOOH Programmatic',
          type: 'dooh',
          platform: 'Vistar Media',
          status: 'Active',
          budget: 150000,
          startDate: '2024-04-01',
          endDate: '2024-10-31',
          metrics: {
            spend: 142800,
            impressions: 8920000,
            cpm: 16.01,
            reach: 2840000,
            frequency: 3.14,
            clicks: 0,
            ctr: 0,
            cpc: 0,
            plays: 8920000
          }
        },
        {
          id: 5,
          name: 'Podcast Audio',
          type: 'audio',
          platform: 'Acast',
          status: 'Completed',
          budget: 95000,
          startDate: '2024-05-01',
          endDate: '2024-08-31',
          metrics: {
            spend: 92680,
            impressions: 2660000,
            cpm: 34.84,
            reach: 1260000,
            frequency: 2.11,
            clicks: 12400,
            ctr: 0.466,
            cpc: 7.47,
            plays: 2380000,
            ltr: 89.47
          }
        }
      ]
    }
  ];

  const _toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const toggleRowSelection = (id: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const toggleAllRows = (items: any[]) => {
    const allIds = items.map(item => item.id);
    if (allIds.every(id => selectedRows.has(id))) {
      // Deselect all
      const newSelected = new Set(selectedRows);
      allIds.forEach(id => newSelected.delete(id));
      setSelectedRows(newSelected);
    } else {
      // Select all
      const newSelected = new Set(selectedRows);
      allIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toFixed(0);
  };

  const formatCurrency = (num: number): string => {
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(0)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  const formatPercent = (num: number): string => {
    return `${num.toFixed(2)}%`;
  };

  const getBeatTypeColor = (type: BeatType): string => {
    const colors: Record<BeatType, string> = {
      'paid-social': 'bg-blue-500',
      'influencer': 'bg-purple-500',
      'ctv': 'bg-orange-500',
      'dooh': 'bg-green-500',
      'audio': 'bg-pink-500',
      'olv': 'bg-cyan-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getBeatTypeLabel = (type: BeatType): string => {
    const labels: Record<BeatType, string> = {
      'paid-social': 'Paid Social',
      'influencer': 'Influencer',
      'ctv': 'CTV',
      'dooh': 'DOOH',
      'audio': 'Audio',
      'olv': 'OLV'
    };
    return labels[type] || type;
  };

  const getPlatformIcon = (platform: string) => {
    const platformLower = platform.toLowerCase();
    
    if (platformLower.includes('tiktok')) {
      return <Music2 className="w-4 h-4" />;
    } else if (platformLower.includes('instagram')) {
      return <Instagram className="w-4 h-4" />;
    } else if (platformLower.includes('meta') || platformLower.includes('facebook')) {
      return <Instagram className="w-4 h-4" />;
    } else if (platformLower.includes('ctv') || platformLower.includes('trade desk')) {
      return <Tv className="w-4 h-4" />;
    } else if (platformLower.includes('dooh') || platformLower.includes('vistar')) {
      return <Radio className="w-4 h-4" />;
    } else if (platformLower.includes('audio') || platformLower.includes('acast') || platformLower.includes('podcast')) {
      return <AudioWaveform className="w-4 h-4" />;
    } else {
      return <Play className="w-4 h-4" />;
    }
  };

  return (
    <div className={cn(
      (viewLevel === 'beat' || viewLevel === 'adset' || viewLevel === 'ad' || viewLevel === 'charts') && selectedCampaign ? "" : "max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-8"
    )}>
      {/* Hero Banner - Hide when in beat/adset/ad/charts view */}
      {!((viewLevel === 'beat' || viewLevel === 'adset' || viewLevel === 'ad' || viewLevel === 'charts') && selectedCampaign) && (
        <>
          <Card className="mb-4 sm:mb-6 overflow-hidden border-0 shadow-sm">
        <div 
          className="h-32 sm:h-48 bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 relative"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/80 via-purple-500/80 to-orange-500/80"></div>
          <div className="relative h-full flex flex-col justify-end p-4 sm:p-8">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full w-fit mb-2 sm:mb-3">
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              <span className="text-white text-xs sm:text-sm">Evolve</span>
            </div>
            <h1 className="text-white text-xl sm:text-4xl mb-1 sm:mb-2">DASHBOARDS</h1>
            <p className="text-white/90 text-xs sm:text-sm">Omnichannel reporting across all campaigns</p>
          </div>
        </div>
      </Card>

      {/* Filters & Controls */}
      <Card className="mb-4 sm:mb-6 border-0 shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last-7-days">Last 7 Days</SelectItem>
                  <SelectItem value="last-30-days">Last 30 Days</SelectItem>
                  <SelectItem value="last-90-days">Last 90 Days</SelectItem>
                  <SelectItem value="this-quarter">This Quarter</SelectItem>
                  <SelectItem value="this-year">This Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>

              <Select value={beatTypeFilter} onValueChange={setBeatTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Beat Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Beat Types</SelectItem>
                  <SelectItem value="paid-social">Paid Social</SelectItem>
                  <SelectItem value="influencer">Influencer</SelectItem>
                  <SelectItem value="ctv">CTV</SelectItem>
                  <SelectItem value="dooh">DOOH</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" className="w-full md:w-auto">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Horizontal Tab Navigation - Meta Ads Manager Style */}
      <Card className="mb-4 border-0 shadow-sm overflow-hidden">
        <div className="flex items-center bg-white border-b">
          <button
            onClick={() => {
              setViewLevel('campaign');
              setSelectedCampaign(null);
              setSelectedBeat(null);
              setSelectedAdset(null);
            }}
            className={`px-6 py-3 text-sm transition-all relative ${
              viewLevel === 'campaign'
                ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Campaigns</span>
            </div>
          </button>
          
          <button
            onClick={() => {
              if (selectedCampaign) {
                setViewLevel('beat');
              }
            }}
            disabled={!selectedCampaign}
            className={`px-6 py-3 text-sm transition-all relative ${
              viewLevel === 'beat'
                ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                : selectedCampaign
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Beats</span>
            </div>
          </button>
          
          <button
            onClick={() => {
              if (selectedBeat) {
                setViewLevel('adset');
              }
            }}
            disabled={!selectedBeat}
            className={`px-6 py-3 text-sm transition-all relative ${
              viewLevel === 'adset'
                ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                : selectedBeat
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Adsets</span>
            </div>
          </button>
          
          <button
            onClick={() => {
              if (selectedAdset) {
                setViewLevel('ad');
              }
            }}
            disabled={!selectedAdset}
            className={`px-6 py-3 text-sm transition-all relative ${
              viewLevel === 'ad'
                ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                : selectedAdset
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Ads & Collabs</span>
            </div>
          </button>
          
          <button
            onClick={() => {
              setViewLevel('charts');
            }}
            className={`px-6 py-3 text-sm transition-all relative ${
              viewLevel === 'charts'
                ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Charts</span>
            </div>
          </button>
        </div>
      </Card>
        </>
      )}

      {/* Campaign Level View */}
      {viewLevel === 'campaign' && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <Checkbox 
                      checked={campaigns.every(c => selectedRows.has(c.id))}
                      onCheckedChange={() => toggleAllRows(campaigns)}
                    />
                  </th>
                  <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-600">On/Off</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Budget</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Impressions</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Reach</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Spend</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Starts</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Ends</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr 
                    key={campaign.id} 
                    className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedCampaign(campaign.id);
                      setViewLevel('beat');
                    }}
                  >
                    <td className="px-4 py-3">
                      <Checkbox 
                        checked={selectedRows.has(campaign.id)}
                        onCheckedChange={() => {
                          toggleRowSelection(campaign.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div 
                        className={`w-10 h-6 rounded-full transition-colors ${
                          campaign.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'
                        } relative cursor-pointer`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={`absolute top-1 ${
                          campaign.status === 'Active' ? 'right-1' : 'left-1'
                        } w-4 h-4 rounded-full bg-white transition-all`}></div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{campaign.name}</span>
                        <Badge className={`${ 
                          campaign.status === 'Active' ? 'bg-green-100 text-green-700' :
                          campaign.status === 'Paused' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        } border-0 text-xs`}>
                          {campaign.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {campaign.primaryKPI}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{formatCurrency(campaign.totalBudget)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatNumber(campaign.metrics.totalImpressions)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatNumber(campaign.metrics.totalReach)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(campaign.metrics.totalSpend)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">
                      {new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">
                      {new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Campaign Detail View with Sidebar (Beats, Adsets, Ads, Charts) */}
      {(viewLevel === 'beat' || viewLevel === 'adset' || viewLevel === 'ad' || viewLevel === 'charts') && selectedCampaign && (() => {
        const campaign = campaigns.find(c => c.id === selectedCampaign);
        const beats = campaign?.beats?.filter(beat => beatTypeFilter === 'all' || beat.type === beatTypeFilter) || [];
        
        return (
          <div className="flex h-screen">
            {/* Campaign Sidebar */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
              {/* Back button */}
              <div className="p-4 border-b">
                <button
                  onClick={() => {
                    setViewLevel('campaign');
                    setSelectedCampaign(null);
                    setCampaignSubPage('overview');
                  }}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('dashboards.backToCampaigns')}
                </button>
              </div>

              {/* Campaign Info */}
              <div className="p-4 border-b">
                <h3 className="font-semibold text-[#101828] text-sm mb-1 truncate">{campaign?.name}</h3>
                <Badge variant={campaign?.status === 'Active' ? 'default' : 'secondary'} className="text-xs">
                  {campaign?.status}
                </Badge>
              </div>

              {/* Campaign Navigation Items */}
              <nav className="flex-1 p-4 space-y-1">
                <button
                  onClick={() => setCampaignSubPage('overview')}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                    campaignSubPage === 'overview'
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-sm">{t('dashboards.navOverview')}</span>
                </button>

                <button
                  onClick={() => setCampaignSubPage('suggestions')}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                    campaignSubPage === 'suggestions'
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">{t('dashboards.navSuggestions')}</span>
                </button>

                <button
                  onClick={() => setCampaignSubPage('publications')}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                    campaignSubPage === 'publications'
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{t('campaigns.publicationsTab')}</span>
                </button>

                <button
                  onClick={() => setCampaignSubPage('contentApproval')}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                    campaignSubPage === 'contentApproval'
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">{t('dashboards.navDrafts')}</span>
                </button>

                <button
                  onClick={() => setCampaignSubPage('results')}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                    campaignSubPage === 'results'
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-sm">{t('dashboards.navResults')}</span>
                </button>
              </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                {/* Campaign Header */}
                <div className="mb-8">
                  <h1 className="text-gray-900 mb-4">Frøya - Julkampanj 🇳🇴 🎄 🐟</h1>
                  
                  {/* Stats Row */}
                  <div className="grid grid-cols-5 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Enrolled Influencers</div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">3</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Total Beats</div>
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{campaign?.beats?.length || 0}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Start Date</div>
                      <div className="text-sm text-gray-900">{campaign?.startDate}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">End Date</div>
                      <div className="text-sm text-gray-900">{campaign?.endDate}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Budget</div>
                      <div className="text-sm text-gray-900">{formatCurrency(campaign?.totalBudget || 0)}</div>
                    </div>
                  </div>
                </div>

                {campaignSubPage === 'overview' && (
                  <div className="space-y-6">
                    {/* Status Section */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h2 className="text-gray-900 mb-4">Status</h2>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-gray-700">Finding creators, outreach and negotiation</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-gray-700">Generating and reviewing content</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-gray-700">Publishing content</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-gray-700">Results measurement and analytics</span>
                        </div>
                      </div>
                    </div>

                    {/* Beats Performance Table */}
                    <Card className="border-0 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <Checkbox 
                        checked={beats.every(b => selectedRows.has(b.id))}
                        onCheckedChange={() => toggleAllRows(beats)}
                      />
                    </th>
                    <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-600">On/Off</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Beat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Budget</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">CPM</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Spend</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Starts</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Ends</th>
                  </tr>
                </thead>
                <tbody>
                  {beats.map((beat) => (
                    <tr 
                      key={beat.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedBeat(beat.id);
                        setViewLevel('adset');
                      }}
                    >
                      <td className="px-4 py-3">
                        <Checkbox 
                          checked={selectedRows.has(beat.id)}
                          onCheckedChange={() => {
                            toggleRowSelection(beat.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div 
                          className={`w-10 h-6 rounded-full transition-colors ${
                            beat.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'
                          } relative cursor-pointer`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={`absolute top-1 ${
                            beat.status === 'Active' ? 'right-1' : 'left-1'
                          } w-4 h-4 rounded-full bg-white transition-all`}></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-1 h-8 rounded-full ${getBeatTypeColor(beat.type)}`}></div>
                          <div className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 text-gray-600">
                            {getPlatformIcon(beat.platform)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{beat.name}</span>
                              <Badge className={`${getBeatTypeColor(beat.type)} text-white border-0 text-xs`}>
                                {getBeatTypeLabel(beat.type)}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              {beat.platform}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatCurrency(beat.budget)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatNumber(beat.metrics.impressions)}</td>
                      <td className="px-4 py-3 text-sm text-right">${beat.metrics.cpm.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatNumber(beat.metrics.reach)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(beat.metrics.spend)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {new Date(beat.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {new Date(beat.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
                  </div>
                )}
        
        {/* Placeholder for other tabs */}
        {campaignSubPage === 'suggestions' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 mb-2">{t('dashboards.suggestionsHeading')}</h3>
              <p className="text-sm text-gray-600">{t('dashboards.suggestionsDescription')}</p>
            </CardContent>
          </Card>
        )}

        {campaignSubPage === 'publications' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 mb-2">{t('campaigns.publicationsTab')}</h3>
              <p className="text-sm text-gray-600">{t('campaigns.publicationsPlaceholderDescription')}</p>
            </CardContent>
          </Card>
        )}

        {campaignSubPage === 'contentApproval' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 mb-2">{t('dashboards.draftsHeading')}</h3>
              <p className="text-sm text-gray-600">{t('dashboards.draftsDescription')}</p>
            </CardContent>
          </Card>
        )}

        {campaignSubPage === 'results' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 mb-2">{t('dashboards.resultsHeading')}</h3>
              <p className="text-sm text-gray-600">{t('dashboards.resultsDescription')}</p>
            </CardContent>
          </Card>
        )}

        {/* Adsets View (when viewLevel === 'adset') */}
        {viewLevel === 'adset' && selectedBeat && (() => {
          const beat = campaigns
            .find(c => c.id === selectedCampaign)?.beats
            ?.find(b => b.id === selectedBeat);
          const adsets = beat?.adsets || [];
          
          return (
            <Card className="border-0 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <Checkbox 
                          checked={adsets.every(a => selectedRows.has(a.id))}
                          onCheckedChange={() => toggleAllRows(adsets)}
                        />
                      </th>
                      <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-600">On/Off</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Ad Set</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Targeting</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Impressions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Reach</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Clicks</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">CTR</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Spend</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">CPM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adsets.map((adset) => (
                      <tr 
                        key={adset.id} 
                        className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedAdset(adset.id);
                          setViewLevel('ad');
                        }}
                      >
                        <td className="px-4 py-3">
                          <Checkbox 
                            checked={selectedRows.has(adset.id)}
                            onCheckedChange={() => {
                              toggleRowSelection(adset.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div 
                            className="w-10 h-6 rounded-full transition-colors bg-green-500 relative cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white transition-all"></div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">{adset.name}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              <span>{adset.geo}</span>
                              <span>•</span>
                              <Smartphone className="w-3 h-3" />
                              <span>{adset.deviceType}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs">
                            <div className="text-sm">{adset.targetingSummary}</div>
                            <div className="text-gray-500 mt-1">{adset.placement}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{formatNumber(adset.metrics.impressions)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatNumber(adset.metrics.reach)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatNumber(adset.metrics.clicks)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatPercent(adset.metrics.ctr)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(adset.metrics.spend)}</td>
                        <td className="px-4 py-3 text-sm text-right">${adset.metrics.cpm.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })()}

        {/* Ads View (when viewLevel === 'ad') */}
        {viewLevel === 'ad' && selectedAdset && (() => {
          const adset = campaigns
            .find(c => c.id === selectedCampaign)?.beats
            ?.find(b => b.id === selectedBeat)?.adsets
            ?.find(a => a.id === selectedAdset);
          const ads = adset?.ads || [];
          
          return (
            <Card className="border-0 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <Checkbox 
                          checked={ads.every(a => selectedRows.has(a.id))}
                          onCheckedChange={() => toggleAllRows(ads)}
                        />
                      </th>
                      <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-600">On/Off</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Ad / Creative</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Impressions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Reach</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Engagements</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Eng. Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Video Views</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">VTR</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Spend</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">CPE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.map((ad) => (
                      <tr 
                        key={ad.id} 
                        className="border-b hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <Checkbox 
                            checked={selectedRows.has(ad.id)}
                            onCheckedChange={() => {
                              toggleRowSelection(ad.id);
                            }}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div 
                            className="w-10 h-6 rounded-full transition-colors bg-green-500 relative cursor-pointer"
                          >
                            <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white transition-all"></div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-16 h-16 rounded-lg bg-gray-100 bg-cover bg-center flex-shrink-0"
                              style={{ backgroundImage: `url(${ad.creativeThumbnail})` }}
                            />
                            <div className="min-w-0">
                              <div className="text-sm truncate">{ad.name}</div>
                              {ad.influencerHandle && (
                                <div className="text-xs text-purple-600 mt-0.5">{ad.influencerHandle}</div>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="bg-gray-100 text-gray-700 border-0 text-xs">
                                  {ad.format}
                                </Badge>
                                {ad.deliverableStatus && (
                                  <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                    {ad.deliverableStatus}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">ID: {ad.creativeId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{formatNumber(ad.metrics.impressions)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatNumber(ad.metrics.reach)}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div>{formatNumber(ad.metrics.totalEngagements || 0)}</div>
                          <div className="text-xs text-gray-500 flex items-center justify-end gap-1 mt-1">
                            <Heart className="w-3 h-3" /> {formatNumber(ad.metrics.likes || 0)}
                            <MessageCircle className="w-3 h-3 ml-1" /> {formatNumber(ad.metrics.comments || 0)}
                            <Share2 className="w-3 h-3 ml-1" /> {formatNumber(ad.metrics.shares || 0)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{formatPercent(ad.metrics.engagementRate || 0)}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div>{formatNumber(ad.metrics.videoViews || 0)}</div>
                          {ad.metrics.avgWatchTime && (
                            <div className="text-xs text-gray-500 mt-1">{ad.metrics.avgWatchTime}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{ad.metrics.vtr ? formatPercent(ad.metrics.vtr) : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(ad.metrics.spend)}</td>
                        <td className="px-4 py-3 text-sm text-right">${(ad.metrics.cpe || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })()}

        {/* Charts View */}
        {viewLevel === 'charts' && campaignSubPage === 'overview' && (() => {
          // 1. Spend vs Engagement Performance
          const spendEngagementData = [
            { campaign_name: 'Trail Running Series', money_spend: 98500, engagement: 654200, impression: 12800000, engagementRate: 5.1 },
            { campaign_name: 'Urban Hiking Q4', money_spend: 125800, engagement: 892400, impression: 18200000, engagementRate: 4.9 },
            { campaign_name: 'Weekend Adventures', money_spend: 115400, engagement: 782100, impression: 16400000, engagementRate: 4.8 }
          ];
          
          // 2. CTR Trend Over Time (Last 30 days)
          const ctrTrendData = [
          { date: 'Nov 23', CTR: 0.28, clicks: 12400, impression: 4428571 },
          { date: 'Nov 28', CTR: 0.31, clicks: 14200, impression: 4580645 },
          { date: 'Dec 3', CTR: 0.29, clicks: 13100, impression: 4517241 },
          { date: 'Dec 8', CTR: 0.33, clicks: 15800, impression: 4787879 },
          { date: 'Dec 13', CTR: 0.35, clicks: 16500, impression: 4714286 },
          { date: 'Dec 18', CTR: 0.37, clicks: 17200, impression: 4648649 },
          { date: 'Dec 22', CTR: 0.39, clicks: 18100, impression: 4641026 }
        ];
        
        // 3. Engagement Rate by Campaign (calculated: engagement/impression)
        const engagementRateData = [
          { campaign_name: 'Trail Running Series', engagementRate: 5.1, engagement: 654200, impression: 12800000, likes: 482100, comments: 98200, clicks: 42800 },
          { campaign_name: 'Urban Hiking Q4', engagementRate: 4.9, engagement: 892400, impression: 18200000, likes: 658200, comments: 124800, clicks: 56400 },
          { campaign_name: 'Weekend Adventures', engagementRate: 4.8, engagement: 782100, impression: 16400000, likes: 578900, comments: 108200, clicks: 48900 },
          { campaign_name: 'Outdoor Lifestyle', engagementRate: 4.6, engagement: 518900, impression: 11200000, likes: 382400, comments: 74200, clicks: 34500 },
          { campaign_name: 'City Explorer Launch', engagementRate: 3.7, engagement: 715800, impression: 19500000, likes: 528400, comments: 98600, clicks: 44200 }
        ];
        
        // 4. Video Completion Funnel (showing drop-off at each milestone)
        const videoFunnelData = [
          { milestone: '25%', count: 8920000, percentage: 100, label: 'video_25p' },
          { milestone: '50%', count: 7245000, percentage: 81.2, label: 'video_50p' },
          { milestone: '75%', count: 5384000, percentage: 60.4, label: 'video_75p' },
          { milestone: '95%', count: 3128000, percentage: 35.1, label: 'video_95P' },
          { milestone: '100%', count: 2456000, percentage: 27.5, label: 'video_100p' }
        ];
        
        // 5. Cost Efficiency Matrix (CPM vs CPC)
        const costEfficiencyData = [
          { campaign_name: 'Urban Hiking Q4', CPM: 14.2, CPC: 4.85, money_spend: 125800, CTR: 0.31 },
          { campaign_name: 'Trail Running Series', CPM: 12.8, CPC: 4.12, money_spend: 98500, CTR: 0.33 },
          { campaign_name: 'City Explorer Launch', CPM: 15.4, CPC: 5.42, money_spend: 142300, CTR: 0.28 },
          { campaign_name: 'Outdoor Lifestyle', CPM: 13.1, CPC: 4.28, money_spend: 87200, CTR: 0.31 },
          { campaign_name: 'Weekend Adventures', CPM: 14.8, CPC: 4.68, money_spend: 115400, CTR: 0.32 },
          { campaign_name: 'Mountain Trails', CPM: 16.2, CPC: 5.85, money_spend: 68900, CTR: 0.27 },
          { campaign_name: 'Urban Explorer', CPM: 11.9, CPC: 3.82, money_spend: 92400, CTR: 0.35 }
        ];
        
        // 6. Reach vs Frequency Distribution
        const reachFrequencyData = [
          { campaign_name: 'Urban Hiking Q4', reach: 6250000, frequency: 2.91, impression: 18200000, money_spend: 125800 },
          { campaign_name: 'Trail Running Series', reach: 4850000, frequency: 2.64, impression: 12800000, money_spend: 98500 },
          { campaign_name: 'City Explorer Launch', reach: 7120000, frequency: 2.74, impression: 19500000, money_spend: 142300 },
          { campaign_name: 'Outdoor Lifestyle', reach: 4280000, frequency: 2.62, impression: 11200000, money_spend: 87200 },
          { campaign_name: 'Weekend Adventures', reach: 5940000, frequency: 2.76, impression: 16400000, money_spend: 115400 }
        ];
        
        const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e'];
        
        return (
          <div className="space-y-6">
            {/* Header with description */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-gray-900 mb-2">Advanced Analytics Charts</h2>
                    <p className="text-sm text-gray-600">Key performance indicators and trends based on your campaign metrics</p>
                  </div>
                  <Button variant="outline" className="gap-2">
                    <Download className="w-4 h-4" />
                    Export All Charts
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Chart Grid - 2x3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* 1. Spend vs Engagement Performance */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Spend vs Engagement Performance</h3>
                    <p className="text-xs text-gray-500">Budget allocation (money_spend) compared to total engagement by campaign</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={spendEngagementData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="campaign_name" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={80} />
                      <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          const numValue = Number(value);
                          if (name === 'money_spend') return [`$${(numValue / 1000).toFixed(1)}K`, 'Spend'];
                          if (name === 'engagement') return [`${(numValue / 1000).toFixed(1)}K`, 'Engagements'];
                          return [numValue, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar yAxisId="left" dataKey="money_spend" fill="#3b82f6" name="Spend" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="engagement" fill="#10b981" name="Engagement" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-800">
                      <span className="font-semibold">💡 Insight:</span> Trail Running Series delivers best engagement-to-spend ratio at 5.1% with $98.5K spend.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* 2. CTR Trend Over Time */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Click-Through Rate (CTR) Trend</h3>
                    <p className="text-xs text-gray-500">CTR performance over time (clicks / impression)</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={ctrTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `${value.toFixed(2)}%`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          if (name === 'CTR') return [`${Number(value).toFixed(2)}%`, 'CTR'];
                          return [Number(value).toLocaleString(), name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="CTR" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 5, fill: '#8b5cf6' }} name="CTR (%)" />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-800">
                      <span className="font-semibold">💡 Insight:</span> CTR improved 39% from 0.28% to 0.39%, indicating better ad targeting and creative optimization.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* 3. Engagement Rate by Campaign */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Engagement Rate by Campaign</h3>
                    <p className="text-xs text-gray-500">Total engagement / impression × 100</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={engagementRateData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                      <YAxis dataKey="campaign_name" type="category" stroke="#9ca3af" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          if (name === 'engagementRate') return [`${Number(value).toFixed(2)}%`, 'Engagement Rate'];
                          return [Number(value).toLocaleString(), name];
                        }}
                      />
                      <Bar dataKey="engagementRate" fill="#10b981" radius={[0, 4, 4, 0]}>
                        {engagementRateData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <span className="font-semibold">💡 Insight:</span> Trail Running Series leads with 5.1% engagement rate - 38% higher than lowest performer.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* 4. Video Completion Funnel */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Video Completion Funnel</h3>
                    <p className="text-xs text-gray-500">Viewer retention: video_25p → video_50p → video_75p → video_95P → video_100p</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={videoFunnelData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="milestone" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          if (name === 'count') return [`${(Number(value) / 1000000).toFixed(2)}M views`, 'Views'];
                          if (name === 'percentage') return [`${Number(value).toFixed(1)}%`, 'Retention'];
                          return [value, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Area type="monotone" dataKey="count" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} name="Views" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-800">
                      <span className="font-semibold">💡 Insight:</span> 27.5% completion rate (video_100p) indicates strong content. 81% watch past 50%.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* 5. Cost Efficiency Matrix */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Cost Efficiency Matrix</h3>
                    <p className="text-xs text-gray-500">CPM vs CPC comparison (bubble size = money_spend)</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        type="number" 
                        dataKey="CPM" 
                        name="CPM" 
                        stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                        label={{ value: 'CPM ($)', position: 'insideBottom', offset: -10, style: { fontSize: 11 } }}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="CPC" 
                        name="CPC" 
                        stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                        label={{ value: 'CPC ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                      />
                      <Tooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          if (name === 'CPM' || name === 'CPC') return [`$${Number(value).toFixed(2)}`, name];
                          if (name === 'CTR') return [`${Number(value).toFixed(2)}%`, name];
                          return [`$${(Number(value) / 1000).toFixed(1)}K`, 'Spend'];
                        }}
                      />
                      <Scatter name="Campaigns" data={costEfficiencyData} fill="#8b5cf6">
                        {costEfficiencyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                    <p className="text-xs text-indigo-800">
                      <span className="font-semibold">💡 Insight:</span> Urban Explorer achieves lowest costs (CPM: $11.90, CPC: $3.82) - ideal benchmark.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* 6. Reach vs Frequency Distribution */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="text-gray-900 mb-1">Reach vs Frequency Distribution</h3>
                    <p className="text-xs text-gray-500">Balance between unique reach and ad exposure frequency</p>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={reachFrequencyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="campaign_name" stroke="#9ca3af" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={80} />
                      <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        formatter={(value: any, name: string) => {
                          if (name === 'reach') return [`${(Number(value) / 1000000).toFixed(2)}M`, 'Reach'];
                          if (name === 'frequency') return [`${Number(value).toFixed(2)}`, 'Frequency'];
                          return [value, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar yAxisId="left" dataKey="reach" fill="#06b6d4" name="Reach" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="frequency" stroke="#ec4899" strokeWidth={3} dot={{ r: 5, fill: '#ec4899' }} name="Frequency" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-cyan-50 rounded-lg">
                    <p className="text-xs text-cyan-800">
                      <span className="font-semibold">💡 Insight:</span> City Explorer reaches 7.1M people at 2.74 frequency - optimal for brand awareness.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Summary Insights */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-gray-900 mb-4">Key Takeaways & Recommendations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-purple-900 mb-1">Improve CTR Performance</div>
                        <p className="text-xs text-purple-700">CTR grew 39% to 0.39%. Continue A/B testing creative formats and CTAs to maintain momentum.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <BarChart3 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-blue-900 mb-1">Optimize Video Content</div>
                        <p className="text-xs text-blue-700">27.5% complete view rate is strong. Focus on first 10 seconds to reduce 25-50% drop-off.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm text-green-900 mb-1">Budget Reallocation</div>
                        <p className="text-xs text-green-700">Trail Running Series delivers 5.1% engagement at lower cost. Increase budget by 20%.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}
            </div>
          </div>
        </div>
      );
    })()}
    </div>
  );
}